import React from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  UserCheck,
  Video,
  BadgeCheck,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import StarBorder from "../../components/signO/ui/StarBorder";

const KYCPage = () => {
  return (
    <div className="min-h-screen bg-dark text-white p-8 pt-24">
      <Link
        to="/"
        className="fixed top-8 left-8 text-gray-400 hover:text-white transition-colors z-50"
      >
        <ArrowLeft className="w-6 h-6" />
      </Link>

      <div className="max-w-4xl mx-auto space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-600"
          >
            Know Your Signer: The Gold Standard in Identity Assurance
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-400 max-w-2xl mx-auto"
          >
            We enforce a rigorous, one-time KYC verification process to ensure
            every user is who they claim to be, providing bank-grade trust for
            your contracts.
          </motion.p>
        </section>

        {/* Steps */}
        <section className="space-y-12">
          <h2 className="text-3xl font-bold text-center">
            Our 3-Step Identity Gateway
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-green-500/50 transition-colors">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 mb-4">
                <UserCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                1. Document Upload & Matching
              </h3>
              <p className="text-gray-400">
                Securely upload your Aadhaar and PAN Cards for instant
                validation.
              </p>
            </div>

            <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-green-500/50 transition-colors">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 mb-4">
                <Video className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                2. Biometric Liveness Check
              </h3>
              <p className="text-gray-400">
                Complete a quick video confirmation to prevent fraud and
                deepfakes.
              </p>
            </div>

            <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-green-500/50 transition-colors">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 mb-4">
                <BadgeCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">3. Verified Status</h3>
              <p className="text-gray-400">
                Once approved, your profile earns a permanent "Verified" badge
                for all future signings.
              </p>
            </div>
          </div>
        </section>

        {/* Privacy */}
        <section className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 space-y-4">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-green-400" />
              Privacy and Trust
            </h2>
            <p className="text-gray-400 text-lg">
              We prioritize the security of your sensitive KYC data. All
              information is encrypted and used only for identity validation in
              strict adherence to data protection regulations.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center pt-8">
          <Link to="/signup">
            <StarBorder
              as="button"
              color="cyan"
              speed="4s"
              className="text-lg px-8 py-4"
            >
              Start My Verification
            </StarBorder>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KYCPage;
